import React, { useEffect, useState } from "react";
import { CheckOutlined, CloseOutlined } from "@ant-design/icons";
import { Button, Card, Image, Input, Space, Switch, Table, Tag, Typography } from "antd";
import { type TableProps } from "antd/es/table";
import isEqual from "lodash.isequal";
import { useTranslation } from "react-i18next";

import { nutritionActions, RequestStatus, userSelectors } from "@fitness-app/app-store";
import { CLIENT_INGREDIENT_SOURCES, type Ingredient } from "@fitness-app/data-models/entities/Ingredient";

import ModalForm from "~/components/ModalForm/ModalForm";
import { useEntityChange } from "~/hooks/useEntityChange";
import AddIngredientForm from "~/modules/Nutrition/AddIngredientForm/AddIngredientForm";
import { transformModelToEntity } from "~/modules/Nutrition/AddIngredientForm/transformModelToEntity";
import { type AddIngredientFormModel } from "~/modules/Nutrition/AddIngredientForm/types";
import { useEditIngredient } from "~/modules/Nutrition/hooks/useEditIngredient";
import { getIngredientsAccess } from "~/modules/Nutrition/ingredientsAccess";
import { useNutritionCategories } from "~/modules/Nutrition/hooks/useNutritionCategories";
import { useNutritionMeasures } from "~/modules/Nutrition/hooks/useNutritionMeasures";
import { useAppDispatch, useAppSelector } from "~/store/initializeStore";

/** Kolejka weryfikacji: produkty klientek, dodane ręcznie i ze zdjęć etykiety (TRE-2237). */
const VERIFICATION_FILTER = { verified: false, source: CLIENT_INGREDIENT_SOURCES };

const NutritionIngredients = (): React.ReactElement => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("nutrition");
  const [searchValue, setSearchValue] = useState("");
  const [showIngredientForm, toggleIngredientForm] = useState(false);
  const {
    ingredients,
    ingredientsPerPage,
    ingredientsTotalPages,
    ingredientSearchTerm,
    ingredientsStatus,
    ingredientsPage,
    filteredCategories,
  } = useAppSelector((state) => state.nutrition);
  const { categoryMap, categories } = useNutritionCategories();
  const [loading, onSuccess, onFailure, onStart] = useEntityChange();
  const user = useAppSelector((state) => state.user.data);
  const { measuresMap } = useNutritionMeasures();
  const permissions = useAppSelector(userSelectors.getUserPermissions);
  const { canEdit: canEditIngredients, canApprove: canApproveIngredients } = getIngredientsAccess(permissions);
  const [showIngredientsForVerification, setShowIngredientsForVerification] = useState(false);
  const { showEditIngredientModal, setEditModel, closeEditModal, editFormModel, saveUpdatedIngredient, updating } =
    useEditIngredient(canApproveIngredients);

  useEffect(() => {
    if (!showIngredientsForVerification) {
      void dispatch(nutritionActions.fetchIngredients());
    } else {
      void dispatch(nutritionActions.fetchIngredients(VERIFICATION_FILTER));
    }
  }, [dispatch, showIngredientsForVerification]);

  const onClear = () => {
    setSearchValue("");
    dispatch(nutritionActions.setFilteredCategories([]));
    dispatch(nutritionActions.clearSearchingIngredient());
  };

  const onSearch = (value: string) => {
    const trimmed = value.trim();
    if (!trimmed) {
      if (ingredientSearchTerm) {
        onClear();
      }
      return;
    }
    void dispatch(nutritionActions.searchIngredient({ searchTerm: trimmed }));
  };

  const handleChange: TableProps<Ingredient>["onChange"] = (pagination, filters) => {
    if (!isEqual(filteredCategories, filters.mainCategory)) {
      dispatch(nutritionActions.setFilteredCategories((filters.mainCategory as number[]) || []));

      if (ingredientSearchTerm) {
        void dispatch(
          nutritionActions.searchIngredient({
            searchTerm: ingredientSearchTerm,
          }),
        );
      } else {
        void dispatch(
          nutritionActions.fetchIngredients(showIngredientsForVerification ? VERIFICATION_FILTER : undefined),
        );
      }
    } else {
      if (pagination.current !== ingredientsPage) {
        void dispatch(
          nutritionActions.fetchIngredients({
            page: pagination.current,
            ...(showIngredientsForVerification ? VERIFICATION_FILTER : {}),
          }),
        );
      }
    }
  };

  const handleAddIngredient = async (model: AddIngredientFormModel) => {
    onStart();
    try {
      await dispatch(
        nutritionActions.addIngredient({
          ingredient: transformModelToEntity(model, measuresMap, user?.id),
        }),
      ).unwrap();
      onSuccess();
      toggleIngredientForm(false);
    } catch (e) {
      onFailure();
    }
  };

  return (
    <>
      <Card
        title={
          canApproveIngredients ? (
            <Space size={8}>
              <Typography.Text>Pokaż produkty do weryfikacji</Typography.Text>
              <Switch checked={showIngredientsForVerification} onChange={setShowIngredientsForVerification} />
            </Space>
          ) : null
        }
        extra={
          <Button onClick={() => toggleIngredientForm(true)} type="primary">
            {t("addNewProduct")}
          </Button>
        }
      >
        <Space direction="vertical" style={{ width: "100%" }} size={24}>
          <div className="flex w-full items-center">
            <Input.Search
              disabled={showIngredientsForVerification}
              placeholder={t<string>("search")}
              onSearch={onSearch}
              loading={ingredientsStatus === RequestStatus.SEARCHING}
              value={searchValue}
              onChange={(e) => setSearchValue(e.target.value)}
              style={{ width: 300, margin: "20px 0" }}
            />
            {Boolean(ingredientSearchTerm) && (
              <Button onClick={onClear} type="link">
                {t("reset")}
              </Button>
            )}
          </div>
          <Table
            dataSource={ingredients}
            scroll={{ x: true }}
            rowKey="id"
            loading={ingredientsStatus === RequestStatus.FETCHING}
            pagination={{
              pageSize: ingredientsPerPage,
              total: ingredientsTotalPages * ingredientsPerPage,
              current: ingredientsPage,
              showSizeChanger: false,
            }}
            onChange={handleChange}
          >
            {showIngredientsForVerification && (
              <Table.Column<Ingredient>
                title="Zdjęcie"
                dataIndex="image"
                key="image"
                render={(image: string | null) =>
                  image ? <Image src={image} width={48} height={48} style={{ objectFit: "cover" }} /> : null
                }
              />
            )}
            <Table.Column<Ingredient>
              title={t("ingredientsTable.name")}
              dataIndex="name"
              key="name"
              render={(name: string, row) => (
                <Space size={8}>
                  <span>{`${name} ${row.brand ? `(${row.brand})` : ""}`}</span>
                  {row.source === "client_app_ai" ? <Tag color="purple">AI</Tag> : null}
                </Space>
              )}
            />
            <Table.Column<Ingredient>
              title={t("ingredientsTable.category")}
              dataIndex="mainCategory"
              key="mainCategory"
              render={(category: string) => t(`categories.${categoryMap[category || "16"]?.name}`)}
              filterMode="tree"
              filterSearch
              filteredValue={filteredCategories}
              filters={categories}
            />
            <Table.Column<Ingredient>
              title={t("ingredientsTable.servingSize")}
              dataIndex={["packageSize"]}
              key="packageSize"
              render={(packageSize: number) => packageSize || 100}
            />
            <Table.Column<Ingredient>
              title={t("ingredientsTable.calories")}
              dataIndex={["nutrients", "calories"]}
              key="calories"
            />
            <Table.Column title={t("ingredientsTable.protein")} dataIndex={["nutrients", "protein"]} key="protein" />
            <Table.Column title={t("ingredientsTable.carbs")} dataIndex={["nutrients", "carbohydrates"]} key="carbs" />
            <Table.Column title={t("ingredientsTable.fat")} dataIndex={["nutrients", "fat"]} key="fat" />
            {showIngredientsForVerification && (
              <Table.Column<Ingredient>
                title={t("ingredientsTable.verified")}
                dataIndex="verified"
                key="verified"
                render={(isRequired: boolean) =>
                  isRequired ? (
                    <CheckOutlined style={{ color: "#25b10f" }} />
                  ) : (
                    <CloseOutlined style={{ color: "#ED1C24" }} />
                  )
                }
              />
            )}

            {canEditIngredients && (
              <Table.Column<Ingredient>
                title="Opcje"
                dataIndex="action"
                key="action"
                render={(_, row) => (
                  <Space direction="horizontal">
                    <Button onClick={() => setEditModel(row)} type="link">
                      {t("common:button.edit")}
                    </Button>
                    {/* Kasowanie zostaje w kolejce weryfikacji i przy uprawnieniu do
                        zatwierdzania — trigger guard_ingredient_approval i tak
                        przepuszcza DELETE tylko z `nutrition.ingredients.approve`,
                        a zweryfikowany produkt wisi już w jadłospisach klientek. */}
                    {showIngredientsForVerification && canApproveIngredients && !row.verified && (
                      <Button
                        danger
                        onClick={() => {
                          void dispatch(nutritionActions.deleteIngredient(row.id));
                        }}
                        type="link"
                      >
                        {t("common:button.delete")}
                      </Button>
                    )}
                  </Space>
                )}
              />
            )}
          </Table>
        </Space>
      </Card>
      <ModalForm
        open={showIngredientForm}
        onCancel={() => toggleIngredientForm(false)}
        title={t("ingredientForm.title")}
        width={900}
        loading={!!loading}
      >
        <AddIngredientForm onSubmit={handleAddIngredient} />
      </ModalForm>

      <ModalForm
        open={showEditIngredientModal}
        onCancel={closeEditModal}
        title={t("ingredientForm.editTitle")}
        width={900}
        loading={!!updating}
      >
        <AddIngredientForm model={editFormModel} onSubmit={saveUpdatedIngredient} />
      </ModalForm>
    </>
  );
};

export default NutritionIngredients;
